// Admin payments settings — configure Moamalat NUMO gateway.
import { useEffect, useState } from "react";
import api from "@/api/client";
import TopBar from "@/components/TopBar";
import BackButton from "@/components/BackButton";
import { CreditCard, Save, TestTube, Radio, Eye, EyeOff, Loader2, CheckCircle2, XCircle, AlertTriangle } from "lucide-react";

export default function AdminPayments() {
  const [loading, setLoading] = useState(true);
  const [cfg, setCfg] = useState(null);
  const [form, setForm] = useState({});
  const [showSecret, setShowSecret] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");
  const [err, setErr] = useState("");
  const [intents, setIntents] = useState({ intents: [], total: 0, completed: 0 });

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/payments/numo/config");
      setCfg(data);
      setForm({
        enabled: data.enabled,
        mode: data.mode,
        merchant_id: data.merchant_id || "",
        terminal_id: data.terminal_id || "",
        secret_key: "",
        wallet_topup_enabled: data.wallet_topup_enabled,
        order_payment_enabled: data.order_payment_enabled,
        min_topup_lyd: data.min_topup_lyd,
        max_topup_lyd: data.max_topup_lyd,
      });
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل تحميل الإعدادات");
    } finally { setLoading(false); }
    try {
      const { data } = await api.get("/admin/payments/numo/intents", { params: { limit: 30 } });
      setIntents(data);
    } catch { /* ignore */ }
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    setBusy(true); setMsg(""); setErr("");
    const payload = { ...form };
    if (!payload.secret_key) delete payload.secret_key; // never overwrite with empty
    try {
      const { data } = await api.patch("/admin/payments/numo/config", payload);
      setCfg(data);
      setMsg("تم حفظ الإعدادات بنجاح");
      setForm((f) => ({ ...f, secret_key: "" }));
    } catch (e) {
      setErr(e?.response?.data?.detail || "فشل الحفظ");
    } finally { setBusy(false); }
  };

  if (loading || !cfg) return (
    <div className="min-h-screen bg-[var(--surface-alt)]">
      <TopBar/>
      <div className="max-w-4xl mx-auto p-6 flex items-center gap-2 text-sm text-[var(--text-secondary)]">
        <Loader2 size={16} className="animate-spin"/> جاري التحميل...
      </div>
    </div>
  );

  const readyToActivate = form.merchant_id && form.terminal_id && (cfg.secret_key_set || form.secret_key);

  return (
    <div className="min-h-screen bg-[var(--surface-alt)]" data-testid="admin-payments-page">
      <TopBar/>
      <div className="max-w-4xl mx-auto px-4 sm:px-6 pt-4 flex items-center gap-2">
        <BackButton to="/admin" variant="pill"/>
        <div className="flex-1">
          <h1 className="text-2xl font-black" style={{fontFamily:"Cairo"}}>الدفع الإلكتروني</h1>
          <p className="text-xs text-[var(--text-secondary)]">إعداد بوابة معاملات (NUMO) للبطاقات المصرفية</p>
        </div>
      </div>

      <div className="max-w-4xl mx-auto px-4 sm:px-6 pb-20 space-y-4 mt-4">
        {/* Status banner */}
        <div
          className="rounded-3xl overflow-hidden text-white p-5 relative talabya-shadow-float"
          style={{
            background: cfg.enabled
              ? "linear-gradient(135deg, #16A34A 0%, #065F46 100%)"
              : "linear-gradient(135deg, #B45309 0%, #78350F 100%)",
          }}
        >
          <div className="flex items-start gap-3">
            <div className="w-12 h-12 rounded-2xl bg-white/25 flex items-center justify-center">
              {cfg.enabled ? <CheckCircle2 size={22}/> : <AlertTriangle size={22}/>}
            </div>
            <div className="flex-1">
              <div className="font-black text-lg" style={{fontFamily:"Cairo"}}>
                {cfg.enabled ? "البوابة مفعّلة" : "البوابة لم تُفعّل بعد"}
              </div>
              <div className="text-sm text-white/90 mt-1 leading-relaxed">
                {cfg.enabled
                  ? `الدفع الإلكتروني يعمل في وضع ${cfg.mode === "live" ? "الإنتاج" : "التجريبي"}. الرمز السري: ${cfg.secret_key_masked}`
                  : "قم بإدخال Merchant ID و Terminal ID و Secret Key من شركة معاملات، ثم فعّل البوابة."}
              </div>
            </div>
          </div>
          {cfg.enabled && (
            <div className="mt-4 grid grid-cols-3 gap-2 text-xs">
              <Metric label="إجمالي المحاولات" value={intents.total}/>
              <Metric label="مكتملة" value={intents.completed}/>
              <Metric label="نسبة النجاح" value={intents.total ? `${Math.round((intents.completed / intents.total) * 100)}%` : "—"}/>
            </div>
          )}
        </div>

        {/* Credentials */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5" data-testid="numo-credentials">
          <div className="flex items-center gap-2 mb-3">
            <CreditCard size={18} className="text-primary-brand"/>
            <h2 className="font-black text-lg" style={{fontFamily:"Cairo"}}>بيانات الاعتماد</h2>
          </div>
          <div className="space-y-3">
            <Field label="Merchant ID (رقم التاجر)">
              <input
                value={form.merchant_id}
                onChange={(e) => setForm({ ...form, merchant_id: e.target.value.trim() })}
                dir="ltr"
                className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono"
                placeholder="مثال: 10081014649"
                data-testid="input-merchant-id"
              />
            </Field>
            <Field label="Terminal ID (رقم الطرفية)">
              <input
                value={form.terminal_id}
                onChange={(e) => setForm({ ...form, terminal_id: e.target.value.trim() })}
                dir="ltr"
                className="w-full px-4 py-3 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono"
                placeholder="مثال: 99179395"
                data-testid="input-terminal-id"
              />
            </Field>
            <Field label={`Secret Key (المفتاح السري) ${cfg.secret_key_set ? "— " + cfg.secret_key_masked : ""}`}>
              <div className="relative">
                <input
                  type={showSecret ? "text" : "password"}
                  value={form.secret_key}
                  onChange={(e) => setForm({ ...form, secret_key: e.target.value })}
                  dir="ltr"
                  className="w-full px-4 py-3 pr-10 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white focus:border-primary-brand focus:outline-none font-mono"
                  placeholder={cfg.secret_key_set ? "اترك فارغاً للإبقاء على القيمة الحالية" : "المفتاح السري بصيغة HEX"}
                  data-testid="input-secret-key"
                />
                <button
                  type="button"
                  onClick={() => setShowSecret(!showSecret)}
                  className="absolute top-3 left-3 text-[var(--text-secondary)]"
                  aria-label="toggle secret"
                >
                  {showSecret ? <EyeOff size={16}/> : <Eye size={16}/>}
                </button>
              </div>
              <p className="text-[10px] text-[var(--text-secondary)] mt-1">
                يوفّره فريق معاملات (شركة النقود الإلكترونية الليبية). يُخزَّن على السيرفر ولا يُعرض بالكامل في أي مكان.
              </p>
            </Field>
          </div>
        </div>

        {/* Mode + toggles */}
        <div className="bg-white rounded-3xl talabya-shadow-soft p-5">
          <h2 className="font-black text-lg mb-3" style={{fontFamily:"Cairo"}}>الإعدادات العامة</h2>
          <ToggleRow
            testid="toggle-enabled"
            label="تفعيل بوابة الدفع"
            desc="عند التفعيل، يظهر خيار الدفع بالبطاقة للزبائن"
            value={form.enabled}
            onChange={(v) => setForm({ ...form, enabled: v })}
            disabled={!readyToActivate}
            disabledHint="أدخل Merchant ID و Terminal ID و Secret Key أولاً"
          />
          <div className="mt-3 flex items-center gap-2 p-3 bg-[var(--surface-alt)] rounded-2xl">
            <Radio size={16} className="text-primary-brand"/>
            <span className="text-sm font-bold">وضع التشغيل</span>
            <div className="mr-auto flex gap-1">
              {["test", "live"].map((m) => (
                <button
                  key={m}
                  onClick={() => setForm({ ...form, mode: m })}
                  className={`px-3 py-1.5 rounded-lg text-xs font-black ${
                    form.mode === m ? "bg-primary-brand text-white" : "bg-white text-[var(--text-secondary)] border border-[var(--border)]"
                  }`}
                  data-testid={`mode-${m}`}
                >
                  {m === "test" ? <><TestTube size={12} className="inline"/> تجريبي</> : "إنتاج مباشر"}
                </button>
              ))}
            </div>
          </div>
          <ToggleRow
            testid="toggle-wallet-topup"
            label="السماح بشحن المحفظة بالبطاقة"
            desc="يظهر زر الشحن بالبطاقة في صفحة الملف الشخصي"
            value={form.wallet_topup_enabled}
            onChange={(v) => setForm({ ...form, wallet_topup_enabled: v })}
          />
          <ToggleRow
            testid="toggle-order-payment"
            label="السماح بدفع الطلبات مباشرة بالبطاقة"
            desc="يضيف خيار البطاقة إلى شاشة الدفع بجانب الكاش والمحفظة"
            value={form.order_payment_enabled}
            onChange={(v) => setForm({ ...form, order_payment_enabled: v })}
          />

          <div className="mt-3 grid grid-cols-2 gap-2">
            <Field label="الحد الأدنى للشحن (د.ل)">
              <input type="number" value={form.min_topup_lyd}
                onChange={(e) => setForm({ ...form, min_topup_lyd: parseFloat(e.target.value || "0") })}
                dir="ltr"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white text-center font-bold"
                data-testid="input-min-topup"/>
            </Field>
            <Field label="الحد الأقصى للشحن (د.ل)">
              <input type="number" value={form.max_topup_lyd}
                onChange={(e) => setForm({ ...form, max_topup_lyd: parseFloat(e.target.value || "0") })}
                dir="ltr"
                className="w-full px-3 py-2 rounded-xl border border-[var(--border)] bg-[var(--surface-alt)] focus:bg-white text-center font-bold"
                data-testid="input-max-topup"/>
            </Field>
          </div>
        </div>

        {msg && <div className="bg-green-50 text-green-700 px-4 py-3 rounded-xl text-sm font-bold" data-testid="save-msg">{msg}</div>}
        {err && <div className="bg-red-50 text-red-600 px-4 py-3 rounded-xl text-sm font-bold" data-testid="save-err">{err}</div>}

        <button
          onClick={save}
          disabled={busy}
          className="w-full py-4 rounded-2xl bg-primary-brand text-white font-black talabya-shadow-float disabled:opacity-60 flex items-center justify-center gap-2"
          data-testid="save-btn"
        >
          {busy ? <Loader2 size={16} className="animate-spin"/> : <Save size={16}/>}
          {busy ? "جاري الحفظ..." : "حفظ الإعدادات"}
        </button>

        {/* Recent intents */}
        {intents.intents.length > 0 && (
          <div className="bg-white rounded-3xl talabya-shadow-soft overflow-hidden">
            <div className="px-5 py-3 border-b border-[var(--border)] flex items-center justify-between">
              <h3 className="font-black" style={{fontFamily:"Cairo"}}>آخر عمليات الدفع</h3>
              <span className="text-xs text-[var(--text-secondary)]">{intents.intents.length}</span>
            </div>
            <ul className="divide-y divide-[var(--border)]">
              {intents.intents.slice(0, 20).map((it) => (
                <li key={it.id} className="p-3 flex items-center gap-3 text-xs" data-testid={`intent-${it.id}`}>
                  <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${
                    it.status === "completed" ? "bg-green-50 text-green-600"
                    : it.status === "pending" ? "bg-yellow-50 text-yellow-600"
                    : "bg-red-50 text-red-500"
                  }`}>
                    {it.status === "completed" ? <CheckCircle2 size={14}/> : it.status === "pending" ? <Loader2 size={14}/> : <XCircle size={14}/>}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-mono font-bold truncate">{it.reference}</div>
                    <div className="text-[10px] text-[var(--text-secondary)]">{new Date(it.created_at).toLocaleString("ar")}</div>
                  </div>
                  <div className="text-left">
                    <div className="font-black">{it.amount.toFixed(2)} د.ل</div>
                    <div className="text-[10px] text-[var(--text-secondary)]">{it.status}</div>
                  </div>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-xs font-bold text-[var(--text-secondary)] mb-1">{label}</label>
      {children}
    </div>
  );
}

function Metric({ label, value }) {
  return (
    <div className="bg-white/15 rounded-xl p-2 text-center">
      <div className="text-[10px] text-white/80">{label}</div>
      <div className="font-black text-lg">{value}</div>
    </div>
  );
}

function ToggleRow({ label, desc, value, onChange, disabled, disabledHint, testid }) {
  return (
    <div className={`flex items-start gap-3 py-3 border-t border-[var(--border)] first:border-t-0 ${disabled ? "opacity-60" : ""}`} data-testid={testid}>
      <div className="flex-1">
        <div className="font-bold text-sm">{label}</div>
        <div className="text-[11px] text-[var(--text-secondary)]">{disabled ? disabledHint : desc}</div>
      </div>
      <button
        onClick={() => !disabled && onChange(!value)}
        disabled={disabled}
        className={`relative w-12 h-7 rounded-full transition-colors shrink-0 ${value ? "bg-primary-brand" : "bg-gray-300"} disabled:cursor-not-allowed`}
        aria-label={`toggle ${label}`}
      >
        <span className={`absolute top-1 w-5 h-5 rounded-full bg-white shadow transition-all ${value ? "right-1" : "right-6"}`}/>
      </button>
    </div>
  );
}
